import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './Button';
import { useT } from '../lib/i18n';
import { consentNeeded, getConsent, setConsent, subscribeConsent } from '../lib/consent';

/**
 * The cookie question, asked once and answerable in one tap either way.
 *
 * Nothing optional loads before an answer: refusing is exactly as easy as
 * accepting, per the ePrivacy rules, and "Choices" opens the one toggle
 * there honestly is. Strictly necessary storage is stated, not asked about.
 */
export function CookieBanner() {
  const t = useT();
  const [show, setShow] = useState(consentNeeded);
  const [choices, setChoices] = useState(false);
  const [analytics, setAnalytics] = useState(() => getConsent()?.analytics ?? false);

  useEffect(
    () =>
      subscribeConsent(() => {
        setShow(consentNeeded());
        setAnalytics(getConsent()?.analytics ?? false);
      }),
    [],
  );

  if (!show) return null;

  const decide = (value: boolean) => {
    setConsent(value);
    setShow(false);
    setChoices(false);
  };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label={t('consent.title')}
      className="fixed bottom-4 left-4 right-4 z-50 mx-auto flex max-w-[440px] flex-col gap-3 rounded-panel border border-line bg-paper p-4 shadow-menu sm:left-4 sm:right-auto sm:mx-0"
    >
      <div className="flex flex-col gap-1">
        <span className="text-[15px] font-semibold tracking-[-0.01em]">{t('consent.title')}</span>
        <p className="text-pretty text-[13.5px] leading-[1.55] text-muted">
          {t('consent.body')}{' '}
          <Link to="/cookies" className="text-ink underline decoration-line-strong underline-offset-[3px] hover:decoration-ink">
            {t('consent.link')}
          </Link>
        </p>
      </div>

      {choices && (
        <div className="flex flex-col gap-2 border-y border-line py-3">
          <label className="flex items-start justify-between gap-3">
            <span className="flex flex-col">
              <span className="text-[13.5px] font-medium">{t('cookies.necessary')}</span>
              <span className="text-meta-sm text-muted">{t('consent.necessaryNote')}</span>
            </span>
            <input type="checkbox" checked disabled className="mt-0.5 h-4 w-4 accent-[var(--ink)]" aria-label={t('cookies.necessary')} />
          </label>
          <label className="flex cursor-pointer items-start justify-between gap-3">
            <span className="flex flex-col">
              <span className="text-[13.5px] font-medium">{t('cookies.analytics')}</span>
              <span className="text-meta-sm text-muted">{t('consent.analyticsNote')}</span>
            </span>
            <input
              type="checkbox"
              checked={analytics}
              onChange={(e) => setAnalytics(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-[var(--ink)]"
              aria-label={t('cookies.analytics')}
            />
          </label>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {choices ? (
          <Button size="sm" onClick={() => decide(analytics)}>{t('consent.save')}</Button>
        ) : (
          <>
            <Button size="sm" onClick={() => decide(true)}>{t('consent.acceptAll')}</Button>
            <Button size="sm" variant="secondary" onClick={() => decide(false)}>{t('consent.onlyNecessary')}</Button>
          </>
        )}
        <Button size="sm" variant="text" onClick={() => setChoices((c) => !c)}>
          {choices ? t('action.cancel') : t('consent.choices')}
        </Button>
      </div>
    </div>
  );
}
